import type { Dispatch } from 'react';
import { useEffect, useState } from 'react';
import type {
    AdminProcessesListAlertsState,
    IAdminProcess,
    NewOrOldAdminProcess,
    ShowModalType,
} from '@frontend/components/interfaces/adminProcessesList';
import type { ReactPartialDispatch } from '../../../../utils/hooks/useStateWithPartialSet';
import { ReportCategories } from '../../../../../common/enums/reportCategories';
import { ReportStatus } from '../../../../../common/enums/reportStatus';
import { RequestStatus } from '../../../../../common/enums/requestStatus';
import { getDisplayName } from '../../../../../common/utils/getDisplayName';
import { postJson } from '../../../../store/fetch';
import { translateKey } from '../../../../translate';
import { useAbortableFetch } from '../../../../utils/hooks/useAbortableFetch';
import { Button, Paragraph } from '../../../elements/common';
import { ConfirmationModal } from '../../../elements/modals/ConfirmationModal';
import { Modal } from '../../../elements/modals/Modal';
import { AbortProcessModal } from '../AbortProcessModal';
import { ChangeCareProviderModal } from '../ChangeCareProviderModal';
import { CheckGeneralPractitionerModal } from '../CheckGeneralPractitionerModal';

interface IPropsAdminProcessesModalsProps {
    alerts: AdminProcessesListAlertsState;
    processes: NewOrOldAdminProcess[];
    setAlerts: ReactPartialDispatch<AdminProcessesListAlertsState>;
    setProcesses: (processes: NewOrOldAdminProcess[]) => void;
    setShowModal: Dispatch<ShowModalType>;
    setUpdatedProcess: (checkedProcess: { id: string } | null) => void;
    showModal: ShowModalType;
    succeedingConsultation?: IAdminProcess;
}

export function AdminProcessesModals(props: IPropsAdminProcessesModalsProps) {
    const abortableFetch = useAbortableFetch();

    const [dependentRequests, setDependentRequests] = useState<IAdminProcess[]>([]);
    const [firstCheckedProcess, setFirstCheckedProcess] = useState<IAdminProcess | undefined>();

    useEffect(() => {
        if (props.showModal === 'abortProcess') {
            loadDependentRequests();
        }
    }, [props.showModal]);

    useEffect(() => {
        setFirstCheckedProcess(
            props.processes.filter((process) => process.processId && process.checked)[0] as IAdminProcess
        );
    }, [props.processes]);

    if (!firstCheckedProcess) {
        return null;
    }

    return (
        <>
            {props.showModal === 'changeCareprovider' && (
                <ChangeCareProviderModal
                    onClose={async () => {
                        props.setShowModal(null);
                        props.setUpdatedProcess({ id: firstCheckedProcess.id });
                    }}
                    requestId={firstCheckedProcess.id}
                    serviceId={firstCheckedProcess.serviceId}
                />
            )}
            {props.showModal === 'closeProcess' && (
                <Modal
                    actionButtons={[
                        <Button
                            color="secondary"
                            onClick={async () => {
                                props.setShowModal(null);
                            }}
                            outlined
                        >
                            {translateKey('common.no')}
                        </Button>,
                        <Button
                            color="secondary"
                            onClick={async () => {
                                await closeProcess();
                                props.setShowModal(null);
                            }}
                        >
                            {translateKey('common.yes')}
                        </Button>,
                    ]}
                    isOpen
                    onClose={async () => props.setShowModal(null)}
                    size="small"
                    title={translateKey('request.closeProcess')}
                >
                    <Paragraph>
                        {translateKey(
                            'request.confirmCloseProcess',
                            `${props.processes.filter((process) => process.processId && process.checked).length}`
                        )}
                    </Paragraph>
                </Modal>
            )}
            {props.showModal === 'abortProcessMessage' && (
                <AbortProcessModal
                    adminProcess={firstCheckedProcess}
                    onClose={async () => props.setShowModal(null)}
                    onConfirm={async (message) => {
                        props.setShowModal(null);
                        props.setAlerts({
                            exportedFinancialReportsMessage: message,
                            abortProcessCounter: props.alerts.abortProcessCounter + 1,
                        });
                        props.setUpdatedProcess({ id: firstCheckedProcess.id });
                    }}
                    succeedingConsultation={props.succeedingConsultation}
                />
            )}
            {props.showModal === 'abortProcess' && (
                <>
                    {dependentRequests.length === 0 ? (
                        <ConfirmationModal
                            handleNo={async () => props.setShowModal(null)}
                            handleYes={async () => props.setShowModal('abortProcessMessage')}
                            isOpen
                            question={getAbortProcessMessage(firstCheckedProcess)}
                            title={translateKey('common.notice')}
                        />
                    ) : (
                        renderInfoModal(firstCheckedProcess)
                    )}
                </>
            )}
            {props.showModal === 'checkGeneralPractitioner' && (
                <CheckGeneralPractitionerModal
                    onClose={async () => {
                        props.setShowModal(null);
                        props.setUpdatedProcess({ id: firstCheckedProcess.id });
                    }}
                    requesterOrganization={firstCheckedProcess.requesterOrganization.name}
                    requesterOrganizationId={firstCheckedProcess.requesterOrganizationId || null}
                    requesterPractitioner={firstCheckedProcess.requesterPractitioner}
                    requestId={firstCheckedProcess.id}
                />
            )}
        </>
    );

    function renderInfoModal(process: IAdminProcess) {
        return (
            <Modal
                actionButtons={[
                    <Button
                        color="secondary"
                        onClick={async () => props.setShowModal(null)}
                        outlined
                    >
                        {translateKey('common.close')}
                    </Button>,
                ]}
                isOpen
                onClose={async () => props.setShowModal(null)}
                size="small"
                title={translateKey('common.notice')}
            >
                {translateKey(
                    'request.cannotAbortProcessesAdmin',
                    process.processId,
                    getDisplayName(process.patient),
                    dependentRequests
                        .map((request) => request.processId)
                        .sort()
                        .join(', ')
                )}
            </Modal>
        );
    }

    function getAbortProcessMessage(process: IAdminProcess) {
        if (process.succeedingConsultationId && props.succeedingConsultation) {
            return translateKey(
                'request.abortProcessesAdmin',
                process.processId,
                props.succeedingConsultation.processId
            );
        }

        return translateKey('request.abortProcessAdmin', process.processId, getDisplayName(process.patient));
    }

    async function loadDependentRequests() {
        const requests = await new Promise<IAdminProcess[]>((resolve) => {
            props.processes
                .filter((process) => process.processId && process.checked)
                .forEach(async (process) => {
                    const requestsResult: IAdminProcess[] = [];
                    const url = `/api/requests/${process.id}/dependent`;
                    const result = await abortableFetch.getJson(url);
                    requestsResult.push(...result.json.items);
                    resolve(requestsResult);
                });
        });
        setDependentRequests(requests);
    }

    async function closeProcess() {
        const selectedProcesses = props.processes.filter(
            (process) => process.processId && process.checked
        ) as IAdminProcess[];

        let numberOfClosedProcesses = 0;
        let numberOfNotClosedProcesses = 0;
        props.setAlerts({ incorrectActionFeedback: undefined, correctActionFeedback: undefined });

        for (const process of selectedProcesses) {
            const diagnosticReport = process.diagnosticReports[process.diagnosticReports.length - 1];
            if (
                diagnosticReport &&
                diagnosticReport.reviewStatus === ReportStatus.open &&
                diagnosticReport.category !== ReportCategories.intake
            ) {
                const url = `/api/diagnosticReports/${diagnosticReport.id}/close`;
                await postJson(url, {});

                const processes = [...props.processes];
                const index = processes.findIndex((thisProcess) => thisProcess.id === process.id);
                processes[index] = { ...(processes[index] as IAdminProcess), status: RequestStatus.completed };
                (processes[index] as IAdminProcess).diagnosticReports[process.diagnosticReports.length - 1] = {
                    ...diagnosticReport,
                    reviewStatus: ReportStatus.closed,
                };
                props.setProcesses(processes);
                numberOfClosedProcesses++;
            } else {
                numberOfNotClosedProcesses++;
            }
        }
        if (numberOfNotClosedProcesses > 0) {
            props.setAlerts({
                incorrectActionFeedback: translateKey(
                    'request.feedbackProcessesNotClosed',
                    `${numberOfNotClosedProcesses}`
                ),
            });
        }
        if (numberOfClosedProcesses > 0) {
            props.setAlerts({
                correctActionFeedback: translateKey('request.feedbackProcessesClosed', `${numberOfClosedProcesses}`),
            });
        }
    }
}
